<div class="container">
  <sca-module-title class="title-test">
    <h1>HTTP Interceptors Playground</h1>
    <span scaModuleTitlePost>Testing de interceptores HTTP con HttpContext</span>
  </sca-module-title>

  <!-- Configuración de Petición -->
  <section class="card">
    <h2>Configuración de Petición HTTP</h2>
    
    <div class="form-grid">
      <div class="form-group full-width">
        <sca-text-field label="URL">
          <input
            scaNativeControl
            type="text"
            [(ngModel)]="testUrl"
            placeholder="https://api.example.com/endpoint"
          />
        </sca-text-field>
      </div>

      <div class="form-group">
        <label>Método HTTP</label>
        <div class="method-buttons">
          <button 
            sca-button 
            [variant]="testMethod() === 'GET' ? 'primary' : 'border'"
            (click)="testMethod.set('GET')"
          >
            GET
          </button>
          <button 
            sca-button 
            [variant]="testMethod() === 'POST' ? 'primary' : 'border'"
            (click)="testMethod.set('POST')"
          >
            POST
          </button>
          <button 
            sca-button 
            [variant]="testMethod() === 'PUT' ? 'primary' : 'border'"
            (click)="testMethod.set('PUT')"
          >
            PUT
          </button>
          <button 
            sca-button 
            [variant]="testMethod() === 'DELETE' ? 'primary' : 'border'"
            (click)="testMethod.set('DELETE')"
          >
            DELETE
          </button>
        </div>
      </div>

      @if (testMethod() === 'POST' || testMethod() === 'PUT') {
        <div class="form-group full-width">
          <sca-text-field label="Body (JSON)">
            <textarea
              scaNativeControl
              [(ngModel)]="testBody"
              placeholder='{"title": "foo", "body": "bar", "userId": 1}'
              rows="4"
            ></textarea>
          </sca-text-field>
        </div>
      }
    </div>

    <div class="test-section">
      <h3>URLs Predefinidas</h3>
      <div class="predefined-urls">
        @for (preset of predefinedUrls; track preset.url) {
          <button 
            sca-button 
            variant="border"
            (click)="loadPredefinedUrl(preset.url)"
          >
            {{ preset.label }}
          </button>
        }
      </div>
    </div>
  </section>

  <!-- Configuración de Interceptores -->
  <section class="card">
    <h2>Configuración de Interceptores (HttpContext)</h2>
    
    <sca-alert variant="info">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Controla qué interceptores se aplican a la petición HTTP
    </sca-alert>

    <div class="interceptors-grid">
      <!-- Skip Interceptors -->
      <div class="interceptor-group">
        <h3>Skip Interceptors</h3>
        <p class="group-description">Omitir interceptores específicos</p>
        
        <div class="checkbox-list">
          <sca-checkbox [checked]="skipAuth()" (change)="skipAuth.set($event)">
            SKIP_AUTH
          </sca-checkbox>
          <small>No añadir token de autenticación</small>
        </div>

        <div class="checkbox-list">
          <sca-checkbox [checked]="skipMultilanguage()" (change)="skipMultilanguage.set($event)">
            SKIP_MULTILANGUAGE
          </sca-checkbox>
          <small>No añadir headers de idioma</small>
        </div>

        <div class="checkbox-list">
          <sca-checkbox [checked]="skipTraces()" (change)="skipTraces.set($event)">
            SKIP_TRACES
          </sca-checkbox>
          <small>No añadir sessionId, traceId, spanId</small>
        </div>

        <div class="checkbox-list">
          <sca-checkbox [checked]="skipSecurity()" (change)="skipSecurity.set($event)">
            SKIP_SECURITY
          </sca-checkbox>
          <small>No añadir headers de seguridad</small>
        </div>

        <div class="checkbox-list">
          <sca-checkbox [checked]="skipConfig()" (change)="skipConfig.set($event)">
            SKIP_CONFIG
          </sca-checkbox>
          <small>No añadir headers de configuración</small>
        </div>
      </div>

      <!-- Force URL Type -->
      <div class="interceptor-group">
        <h3>Force URL Type</h3>
        <p class="group-description">Forzar tratamiento de URL</p>

        <div class="checkbox-list">
          <sca-checkbox [checked]="forceInternal()" (change)="forceInternal.set($event)">
            FORCE_INTERNAL
          </sca-checkbox>
          <small>Tratar URL como interna</small>
        </div>

        <div class="checkbox-list">
          <sca-checkbox [checked]="forceExternal()" (change)="forceExternal.set($event)">
            FORCE_EXTERNAL
          </sca-checkbox>
          <small>Tratar URL como externa</small>
        </div>

        <div class="reset-actions">
          <button sca-button variant="border" (click)="resetFlags()">
            Reset Flags
          </button>
        </div>
      </div>
    </div>
  </section>

  <!-- Ejecutar Petición -->
  <section class="card">
    <div class="execute-section">
      <button 
        sca-button 
        variant="primary" 
        [disabled]="loading()"
        (click)="executeRequest()"
      >
        @if (loading()) {
          Ejecutando...
        } @else {
          Ejecutar Petición
        }
      </button>

      @if (lastResponse() || errorMessage()) {
        <button 
          sca-button 
          variant="border" 
          (click)="clearResponse()"
        >
          Limpiar
        </button>
      }
    </div>

    @if (errorMessage()) {
      <sca-alert variant="error" style="margin-top: 1rem;">
        <sca-icon icon="error" size="24px" [weight]="300" left />
        <strong>Error:</strong> {{ errorMessage() }}
      </sca-alert>
    }
  </section>

  <!-- Respuesta HTTP -->
  @if (lastResponse(); as response) {
    <section class="card">
      <h2>
        Respuesta HTTP
        <sca-badge [variant]="getStatusBadgeVariant(response.status)" [rounded]="false">
          {{ response.status }}
        </sca-badge>
      </h2>

      <table class="properties-table">
        <thead>
          <tr>
            <th>Propiedad</th>
            <th>Valor</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Status Code</strong></td>
            <td>
              <sca-badge [variant]="getStatusBadgeVariant(response.status)" [rounded]="false">
                {{ response.status }} {{ response.statusText }}
              </sca-badge>
            </td>
          </tr>
          <tr>
            <td><strong>Timestamp</strong></td>
            <td>{{ response.timestamp | date:'medium' }}</td>
          </tr>
          <tr>
            <td><strong>URL</strong></td>
            <td><code>{{ testUrl() }}</code></td>
          </tr>
          <tr>
            <td><strong>Method</strong></td>
            <td><code>{{ testMethod() }}</code></td>
          </tr>
        </tbody>
      </table>

      <div class="test-section">
        <h3>HttpContext Flags Activos</h3>
        <div class="flags-list">
          @if (skipAuth()) { <sca-badge variant="accent" [rounded]="false">SKIP_AUTH</sca-badge> }
          @if (skipMultilanguage()) { <sca-badge variant="accent" [rounded]="false">SKIP_MULTILANGUAGE</sca-badge> }
          @if (skipTraces()) { <sca-badge variant="accent" [rounded]="false">SKIP_TRACES</sca-badge> }
          @if (skipSecurity()) { <sca-badge variant="accent" [rounded]="false">SKIP_SECURITY</sca-badge> }
          @if (skipConfig()) { <sca-badge variant="accent" [rounded]="false">SKIP_CONFIG</sca-badge> }
          @if (forceInternal()) { <sca-badge variant="warning" [rounded]="false">FORCE_INTERNAL</sca-badge> }
          @if (forceExternal()) { <sca-badge variant="warning" [rounded]="false">FORCE_EXTERNAL</sca-badge> }
          @if (!skipAuth() && !skipMultilanguage() && !skipTraces() && !skipSecurity() && !skipConfig() && !forceInternal() && !forceExternal()) {
            <sca-badge variant="default" [rounded]="false">Todos los interceptores activos</sca-badge>
          }
        </div>
      </div>

      <sca-view-more>
        <div slot="title">Response Headers</div>
        <pre class="code-block">{{ response.headers | json }}</pre>
      </sca-view-more>

      <sca-view-more>
        <div slot="title">Response Body</div>
        <pre class="code-block">{{ response.body | json }}</pre>
      </sca-view-more>

      <div class="test-section">
        <h3>Código de Ejemplo</h3>
        <pre class="code-block">{{ generateCodeExample() }}</pre>
        <button 
          sca-button 
          variant="border" 
          (click)="copyToClipboard(generateCodeExample())"
          style="margin-top: 1rem;"
        >
          Copiar Código
        </button>
      </div>
    </section>
  }

  <!-- Ayuda -->
  <section class="card">
    <h2>Ayuda y Recursos</h2>
    
    <sca-alert variant="info">
      <sca-icon icon="lightbulb" size="24px" [weight]="300" left />
      <strong>Tip:</strong> Abre DevTools (F12) → Network para ver los headers reales enviados
    </sca-alert>

    <div class="test-section">
      <h3>Interceptores Disponibles</h3>
      <ul>
        <li><strong>Authentication:</strong> Añade el token Bearer en Authorization</li>
        <li><strong>Multilanguage:</strong> Añade headers Locale y Accept-Language</li>
        <li><strong>Traces:</strong> Añade sessionId, traceId, spanId</li>
        <li><strong>Security:</strong> Añade headers de seguridad personalizados</li>
        <li><strong>Config:</strong> Añade headers de configuración (X-IBM-Client-Id, etc.)</li>
      </ul>
    </div>

    <div class="test-section">
      <h3>APIs Recomendadas para Pruebas</h3>
      <ul>
        <li><strong>httpbin.org/headers:</strong> Muestra todos los headers enviados</li>
        <li><strong>httpbin.org/get:</strong> Endpoint GET con info detallada</li>
        <li><strong>jsonplaceholder.typicode.com:</strong> API REST fake con datos JSON</li>
      </ul>
    </div>
  </section>
</div>
